<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NPopconfirm, NTag } from 'naive-ui'
import { useSourcesStore } from '../stores/sources'
import type { SourceRow, SourceStatus } from '../stores/source_activity'
import ActivityRowDetail from './ActivityRowDetail.vue'

const props = defineProps<{
  row: SourceRow
  // 1-indexed position of this row inside the parent's queued slice; the
  // parent owns the queue ordering so ActivityRow doesn't need to know
  // about the source_activity store. `queueDepth` is the count of all
  // currently queued (not running) rows so the user sees "2 of 3".
  queuePosition?: number
  queueDepth?: number
}>()

const emit = defineEmits<{
  (e: 'open-path', path: string): void
}>()

// Row-preview truncation cutoffs — file previews stay short since the
// filename/content snippet is secondary to the kind badge; prompt previews
// get more room since the prompt text is the primary thing being scanned.
const FILE_PREVIEW_MAX_LENGTH = 60
const PROMPT_PREVIEW_MAX_LENGTH = 80

const sources = useSourcesStore()
const expanded = ref(false)
const cancelling = ref(false)
const cancelError = ref<string | null>(null)

const statusType = computed<'success' | 'error' | 'info' | 'default'>(() => {
  switch (props.row.status) {
    case 'succeeded':
      return 'success'
    case 'failed':
      return 'error'
    case 'running':
      return 'info'
    default:
      return 'default'
  }
})

const statusLabel = computed<string>(() => {
  switch (props.row.status) {
    case 'queued':
      return 'Queued'
    case 'running':
      return 'Running'
    case 'succeeded':
      return 'Done'
    case 'failed':
      return 'Failed'
    default:
      return 'Unknown'
  }
})

const kindBadge = computed(() => kindLabel(props.row.kind))

const preview = computed(() => rowPreview(props.row))

// In-flight rows have no result/answer/pages_touched yet, but the user
// still has questions while the run is happening: "what did I just
// submit?", "is it stuck?". So expansion is allowed any time there is
// at least the prompt body to show, even before terminal data lands.
// Once the row transitions to succeeded/failed and the broadcast updates
// `result`/`error` in place, the same expanded panel re-renders with
// the new sections — no UI flicker because `expanded` is a local ref.
//
// Mirrors ActivityRowDetail's own per-section conditions directly off
// `row` fields (rather than importing its computeds) since this toggle
// button needs an answer before the detail panel is ever rendered.
const hasDetail = computed(() => rowHasDetail(props.row))

function rowHasDetail(row: SourceRow): boolean {
  const promptBody = row.kind === 'url' ? (row.url ?? '') : (row.content ?? '')
  const contentPagesCount = (row.result?.pages_touched ?? []).filter((p) => p !== 'log.md').length
  const isNoOpIngest =
    row.status === 'succeeded' &&
    !!row.result &&
    row.result.routing !== 'query' &&
    !row.result.answer &&
    !row.result.reminder &&
    contentPagesCount === 0
  return (
    promptBody.trim().length > 0 ||
    isInFlight(row.status) ||
    (row.result?.answer?.length ?? 0) > 0 ||
    contentPagesCount > 0 ||
    (row.result?.citations?.length ?? 0) > 0 ||
    isNoOpIngest ||
    !!row.result?.reminder ||
    (row.error?.length ?? 0) > 0
  )
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'web_prompt':
      return 'Ask'
    case 'file_upload':
      return 'File'
    case 'url':
      return 'URL'
    default:
      return kind
  }
}

function rowPreview(row: SourceRow): string {
  if (row.kind === 'url') return row.url ?? '(no url)'
  if (row.kind === 'file_upload')
    return row.content ? truncate(row.content, FILE_PREVIEW_MAX_LENGTH) : '(uploaded file)'
  return truncate(row.content ?? '', PROMPT_PREVIEW_MAX_LENGTH) || '(empty prompt)'
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text
}

function toggleExpand(): void {
  if (hasDetail.value) expanded.value = !expanded.value
}

function isInFlight(status: SourceStatus | null): boolean {
  return status === 'queued' || status === 'running'
}

async function onCancelConfirm(): Promise<void> {
  cancelling.value = true
  cancelError.value = null
  try {
    await sources.cancel(props.row.id)
    // Row transitions to terminal via the SourceChannel broadcast; no
    // local state update needed. If the broadcast is somehow lost, the
    // next drawer-open load() will reconcile from the API.
  } catch (e) {
    cancelError.value = e instanceof Error ? e.message : 'cancel failed'
  } finally {
    cancelling.value = false
  }
}
</script>

<template>
  <li class="border border-white/10 rounded px-3 py-2 flex flex-col gap-2">
    <!-- Row header: expand-toggle button + sibling Cancel button.
         Nesting the NButton inside the outer expand <button> produces
         invalid HTML (no buttons inside buttons) and breaks keyboard
         + screen-reader semantics, so the Cancel control is a sibling. -->
    <div class="flex items-center gap-3">
      <button
        type="button"
        class="flex items-center gap-3 min-w-0 text-left flex-1 bg-transparent border-0 p-0 cursor-pointer disabled:cursor-default"
        :disabled="!hasDetail"
        :aria-expanded="hasDetail ? expanded : undefined"
        @click="toggleExpand"
      >
        <span
          v-if="hasDetail"
          aria-hidden="true"
          class="inline-block w-3 text-white/50 transition-transform select-none"
          :class="{ 'rotate-90': expanded }"
          >›</span
        >
        <span v-else aria-hidden="true" class="inline-block w-3" />
        <NTag size="tiny" round :bordered="false">{{ kindBadge }}</NTag>
        <span class="flex-1 truncate text-sm text-white/80" :class="{ 'italic text-white/40': !preview }">{{
          preview
        }}</span>
        <NTag size="small" round :type="statusType">
          <span
            v-if="isInFlight(row.status)"
            class="inline-block h-1.5 w-1.5 rounded-full bg-current mr-1.5 animate-pulse"
          />
          {{ statusLabel }}
        </NTag>
      </button>

      <NPopconfirm v-if="isInFlight(row.status)" placement="left" @positive-click="onCancelConfirm">
        <template #trigger>
          <NButton
            size="tiny"
            quaternary
            type="error"
            :loading="cancelling"
            :data-testid="`cancel-${row.id}`"
            aria-label="Cancel this ingestion"
            >Cancel</NButton
          >
        </template>
        Cancel this ingestion? In-flight work will be discarded.
      </NPopconfirm>
    </div>

    <p v-if="cancelError" class="text-xs text-red-300 m-0">{{ cancelError }}</p>

    <ActivityRowDetail
      v-if="expanded && hasDetail"
      :row="row"
      :queue-position="queuePosition"
      :queue-depth="queueDepth"
      @open-path="emit('open-path', $event)"
    />
  </li>
</template>
